import utils from "../utils"
class LiveSession {
	constructor(store) {
		this._wss = "wss://db10.gamersky.com/townsquare/";
		// this._wss = "ws://localhost:8081/"; // uncomment if using local server with NODE_ENV=development
		this._socket = null;
		this._isSpectator = true;
		this._gamestate = [];
		this._store = store;
		this._pingInterval = 30 * 1000; // 30 seconds between pings
		this._pingTimer = null;
		this._reconnectTimer = null;
		this._players = {}; // map of players connected to a session
		this._pings = {}; // map of player IDs to ping
		// reconnect to previous session
		if (this._store.state.session.sessionId) {
			this.connect(this._store.state.session.sessionId);
		}
	}

	/**
	 * 为已通过的频道打开新会话.
	 * @param channel
	 * @private
	 */
	_open(channel) {
		this.disconnect();
		this._socket = new WebSocket(
			this._wss +
			channel +
			"/" +
			(this._isSpectator ? this._store.state.session.playerId : "host")
		);
		this._socket.addEventListener("message", this._handleMessage.bind(this));
		this._socket.onopen = this._onOpen.bind(this);
		this._socket.onclose = err => {
			this._socket = null;
			clearInterval(this._pingTimer);
			this._pingTimer = null;
			if (err.code !== 1000) {
				// 连接中断，3秒钟后重新连接
				this._store.commit("session/setReconnecting", true);
				this._reconnectTimer = setTimeout(
					() => this.connect(channel),
					3 * 1000
				);
			} else {
				this._store.commit("session/setSessionId", "");
				if (err.reason) alert(err.reason);
			}
		};
	}

	/**
	 * 通过socket发送消息
	 * @param command
	 * @param params
	 * @private
	 */
	_send(command, params) {
		if (this._socket && this._socket.readyState === 1) {
			this._socket.send(JSON.stringify([command, params]));
		}
	}

	/**
	 * 如果可以，直接向单个playerId发送消息。
	 * 否则就广播。
	 * @param playerId player ID or "host", optional
	 * @param command
	 * @param params
	 * @private
	 */
	_sendDirect(playerId, command, params) {
		if (playerId) {
			this._send("direct", {
				[playerId]: [command, params]
			});
		} else {
			this._send(command, params);
		}
	}

	/**
	 * 打开socket事件处理
	 * @private
	 */
	_onOpen() {
		if (this._isSpectator) {
			this._sendDirect(
				"host",
				"getGamestate",
				this._store.state.session.playerId
			);
		} else {
			this.sendGamestate();
		}
		this._ping();
	}

	/**
	 * 发送带有玩家ID和ST标志的ping消息。
	 * @private
	 */
	_ping() {
		this._handlePing([0, [], false]);
		this._send("ping", [
			this._isSpectator ?
			this._store.state.session.playerId :
			Object.keys(this._players).length,
			"latency"
		]);
		clearTimeout(this._pingTimer);
		this._pingTimer = setTimeout(this._ping.bind(this), this._pingInterval);
	}

	/**
	 * 处理传入的socket消息
	 * @param data
	 * @private
	 */
	_handleMessage({
		data
	}) {
		let command, params;
		try {
			[command, params] = JSON.parse(data);
		} catch (err) {
			console.log("unsupported socket message", data);
		}
		switch (command) {
			case "getGamestate":
				this.sendGamestate(params);
				break;
			case "edition":
				this._updateEdition(params);
				break;
			case "fabled":
				this._updateFabled(params);
				break;
			case "gs":
				this._updateGamestate(params);
				break;
			case "player":
				this._updatePlayer(params);
				break;
			case "claim":
				this._updateSeat(params);
				break;
			case "ping":
				this._handlePing(params);
				break;
			case "nomination":
				if (!this._isSpectator) return;
				if (!params) {
					// create vote history record
					this._store.commit(
						"session/addHistory",
						this._store.state.players.players
					);
				}
				this._store.commit("session/nomination", {
					nomination: params
				});
				break;
			case "swap":
				if (!this._isSpectator) return;
				this._store.commit("players/swap", params);
				break;
			case "move":
				if (!this._isSpectator) return;
				this._store.commit("players/move", params);
				break;
			case "remove":
				if (!this._isSpectator) return;
				this._store.commit("players/remove", params);
				break;
			case "marked":
				if (!this._isSpectator) return;
				this._store.commit("session/setMarkedPlayer", params);
				break;
			case "isNight":
				if (!this._isSpectator) return;
				this._store.commit("toggleNight", params);
				break;
			case "isVoteHistoryAllowed":
				if (!this._isSpectator) return;
				this._store.commit("session/setVoteHistoryAllowed", params);
				this._store.commit("session/clearVoteHistory");
				break;
			case "votingSpeed":
				if (!this._isSpectator) return;
				this._store.commit("session/setVotingSpeed", params);
				break;
			case "clearVoteHistory":
				if (!this._isSpectator) return;
				this._store.commit("session/clearVoteHistory");
				break;
			case "isVoteInProgress":
				if (!this._isSpectator) return;
				this._store.commit("session/setVoteInProgress", params);
				break;
			case "vote":
				this._handleVote(params);
				break;
			case "lock":
				this._handleLock(params);
				break;
			case "bye":
				this._handleBye(params);
				break;
			case "pronouns":
				this._updatePlayerPronouns(params);
				break;
		}
	}

	/**
	 * 以主持人或观众的身份连接到新的现场会议。
	 * 如果还没有唯一的playerId，请设置一个。
	 * @param channel
	 */
	connect(channel) {
		if (!this._store.state.session.playerId) {
			this._store.commit(
				"session/setPlayerId",
				Math.random()
				.toString(36)
				.substr(2)
			);
		}
		this._pings = {};
		this._store.commit("session/setPlayerCount", 0);
		this._store.commit("session/setPing", 0);
		this._isSpectator = this._store.state.session.isSpectator;
		this._open(channel);
	}

	/**
	 * 关闭当前会话（如果有）。
	 */
	disconnect() {
		this._pings = {};
		this._store.commit("session/setPlayerCount", 0);
		this._store.commit("session/setPing", 0);
		this._store.commit("session/setReconnecting", false);
		clearTimeout(this._reconnectTimer);
		if (this._socket) {
			if (this._isSpectator) {
				this._sendDirect("host", "bye", this._store.state.session.playerId);
			}
			this._socket.close(1000);
			this._socket = null;
		}
	}

	/**
	 * 发布当前游戏状态。
	 * 可选参数以减少流量。（仅发送玩家数据）
	 * @param playerId
	 * @param isLightweight
	 */
	sendGamestate(playerId = "", isLightweight = false) {
		if (this._isSpectator) return;
		this._gamestate = this._store.state.players.players.map(player => ({
			name: player.name,
			id: player.id,
			isDead: player.isDead,
			isVoteless: player.isVoteless,
			pronouns: player.pronouns,
			...(player.role && player.role.team === "traveler" ? {
				roleId: player.role.id
			} : {}),
			userInfo: player.userInfo
		}));
		if (isLightweight) {
			this._sendDirect(playerId, "gs", {
				gamestate: this._gamestate,
				isLightweight
			});
		} else {
			const {
				session,
				grimoire
			} = this._store.state;
			const {
				fabled
			} = this._store.state.players;
			this.sendEdition(playerId);
			this._sendDirect(playerId, "gs", {
				gamestate: this._gamestate,
				isNight: grimoire.isNight,
				isVoteHistoryAllowed: session.isVoteHistoryAllowed,
				nomination: session.nomination,
				votingSpeed: session.votingSpeed,
				lockedVote: session.lockedVote,
				isVoteInProgress: session.isVoteInProgress,
				markedPlayer: session.markedPlayer,
				fabled: fabled.map(f => (f.isCustom ? f : {
					id: f.id
				})),
				...(session.nomination ? {
					votes: session.votes
				} : {}),
			});
		}
	}

	/**
	 * 根据传入数据更新游戏状态。
	 * @param data
	 * @private
	 */
	_updateGamestate(data) {
		if (!this._isSpectator) return;
		const {
			gamestate,
			isLightweight,
			isNight,
			isVoteHistoryAllowed,
			nomination,
			votingSpeed,
			votes,
			lockedVote,
			isVoteInProgress,
			markedPlayer,
			fabled
		} = data;
		const players = this._store.state.players.players;
		// adjust number of players
		if (players.length < gamestate.length) {
			for (let x = players.length; x < gamestate.length; x++) {
				this._store.commit("players/add", gamestate[x].name);
			}
		} else if (players.length > gamestate.length) {
			for (let x = players.length; x > gamestate.length; x--) {
				this._store.commit("players/remove", x - 1);
			}
		}
		// 更新每个玩家的状态
		gamestate.forEach((state, x) => {
			const player = players[x];
			const {
				roleId
			} = state;
			// 更新相关属性
			["name", "id", "isDead", "isVoteless", "pronouns", "userInfo"].forEach(property => {
				const value = state[property];
				if (player[property] !== value) {
					this._store.commit("players/update", {
						player,
						property,
						value,
						userInfo: state.userInfo ? state.userInfo : null
					});
				}
			});

			// 角色是特殊的，因为旅行者
			if (roleId && player.role.id !== roleId) {
				const role =
					this._store.state.roles.get(roleId) ||
					this._store.getters.rolesJSONbyId.get(roleId);
				if (role) {
					this._store.commit("players/update", {
						player,
						property: "role",
						value: role,
						userInfo: state.userInfo ? state.userInfo : null
					});
				}
			} else if (!roleId && player.role.team === "traveler") {
				this._store.commit("players/update", {
					player,
					property: "role",
					value: {},
					userInfo: state.userInfo ? state.userInfo : null
				});
			}
		});
		this._store.dispatch("players/clearRoles");
		if (!isLightweight) {
			this._store.commit("toggleNight", !!isNight);
			this._store.commit("session/setVoteHistoryAllowed", isVoteHistoryAllowed);
			this._store.commit("session/nomination", {
				nomination,
				votes,
				votingSpeed,
				lockedVote,
				isVoteInProgress
			});
			this._store.commit("session/setMarkedPlayer", markedPlayer);
			this._store.commit("players/setFabled", {
				fabled: fabled.map(f => this._store.state.fabled.get(f.id) || f)
			});
		}
	}

	/**
	 * 发布剧本更新，只有ST
	 * @param playerId
	 */
	sendEdition(playerId = "") {
		if (this._isSpectator) return;
		const {
			edition
		} = this._store.state;
		let roles;
		if (!edition.isOfficial) {
			roles = this._store.getters.customRolesStripped;
		}
		let userInfo = this._store.state.session.sessionUserInfo ? this._store.state.session.sessionUserInfo : null
		this._sendDirect(playerId, "edition", {
			edition: edition.isOfficial ? {
				id: edition.id
			} : edition,
			...(roles ? {
				roles
			} : {}),
			userInfo: userInfo
		});
	}

	/**
	 * 更新自定义版本的版本和角色。
	 * @param edition
	 * @param roles
	 * @private
	 */
	_updateEdition({
		edition,
		roles,
		userInfo
	}) {
		if (!this._isSpectator) return;
		this._store.commit("setEdition", edition);
		if (userInfo) {
			this._store.commit("session/setSessionUserInfo", userInfo);
		}
		if (roles) {
			this._store.commit("setCustomRoles", roles);
			if (this._store.state.roles.size !== roles.length) {
				const missing = [];
				roles.forEach(({
					id
				}) => {
					if (!this._store.state.roles.get(id)) {
						missing.push(id);
					}
				});
				alert(
					`This session contains custom characters that can't be found. ` +
					`Please load them before joining! ` +
					`Missing roles: ${missing.join(", ")}`
				);
				this.disconnect();
				this._store.commit("toggleModal", "edition");
			}
		}
	}

	/**
	 * 发布更新传奇角色，只有ST
	 */
	sendFabled() {
		if (this._isSpectator) return;
		const {
			fabled
		} = this._store.state.players;
		this._send(
			"fabled",
			fabled.map(f => (f.isCustom ? f : {
				id: f.id
			}))
		);
	}

	/**
	 * 更新传说角色。
	 * @param fabled
	 * @private
	 */
	_updateFabled(fabled) {
		if (!this._isSpectator) return;
		this._store.commit("players/setFabled", {
			fabled: fabled.map(f => this._store.state.fabled.get(f.id) || f)
		});
	}

	/**
	 * 发布更新普通角色
	 * @param player
	 * @param property
	 * @param value
	 */
	sendPlayer({
		player,
		property,
		value
	}) {
		if (this._isSpectator || property === "reminders") return;
		const index = this._store.state.players.players.indexOf(player);
		let userInfo = player.userInfo;
		if (property === "role") {
			if (value.team && value.team === "traveler") {
				// 更新本地游戏状态以将该玩家作为旅行者记住
				this._gamestate[index].roleId = value.id;
				this._send("player", {
					index,
					property,
					value: value.id,
					userInfo: userInfo
				});
			} else if (this._gamestate[index].roleId) {
				// 这个角色以前是个旅行者
				delete this._gamestate[index].roleId;
				this._send("player", {
					index,
					property,
					value: "",
					userInfo: userInfo
				});
			}
		} else {
			this._send("player", {
				index,
				property,
				value,
				userInfo: userInfo
			});
		}
	}

	/**
	 * Update a player based on incoming data. Player only.
	 * 根据掺入数据更新角色，仅限玩家
	 * @param index
	 * @param property
	 * @param value
	 * @private
	 */
	_updatePlayer({
		index,
		property,
		value,
		userInfo
	}) {
		if (!this._isSpectator) return;
		const player = this._store.state.players.players[index];
		if (!player) return;
		// special case where a player stops being a traveler
		// 特殊情况下，玩家不再是旅行者
		if (property === "role") {
			if (!value && player.role.team === "traveler") {
				if (confirm("说书人正在分配角色，是否删除当前角色和标记？")) {
					this._store.dispatch("players/clearRoles");
				}
				// reset to an unknown role
				// 重置为未知角色
				this._store.commit("players/update", {
					player,
					property: "role",
					value: {},
					userInfo: userInfo
				});
			} else {
				// load role, first from session, the global, then fail gracefully
				// 首先从全局会话加载角色，然后优雅地失败
				if (confirm("说书人正在分配角色，是否删除当前角色和标记？")) {
					this._store.dispatch("players/clearRoles");
				}
				const role =
					this._store.state.roles.get(value) ||
					this._store.getters.rolesJSONbyId.get(value) || {};
				this._store.commit("players/update", {
					player,
					property: "role",
					value: role,
					userInfo: userInfo
				});
			}
		} else {
			// 否则只更新玩家
			this._store.commit("players/update", {
				player,
				property,
				value,
				userInfo: userInfo
			});
			// if(property === "id" && value === ""){
			// 	this._store.commit("session/claimSeat", -1);
			// }
		}
	}

	/**
	 * 发布玩家代词更新
	 * @param player
	 * @param value
	 * @param isFromSockets
	 */
	sendPlayerPronouns({
		player,
		value,
		isFromSockets
	}) {
		//send pronoun only for the seated player or storyteller
		// 只为坐着的玩家或说书人发送代词
		//Do not re-send pronoun data for an update that was recieved from the sockets layer
		// 不要为socket接收的更新发送代词
		if (
			isFromSockets ||
			(this._isSpectator && this._store.state.session.playerId !== player.id)
		)
			return;
		const index = this._store.state.players.players.indexOf(player);
		this._send("pronouns", [index, value]);
	}

	/**
	 * Update a pronouns based on incoming data.
	 * 根据收到的数据更新代词
	 * @param index
	 * @param value
	 * @private
	 */
	_updatePlayerPronouns([index, value]) {
		const player = this._store.state.players.players[index];

		this._store.commit("players/update", {
			player,
			property: "pronouns",
			value,
			isFromSockets: true
		});
	}

	/**
	 * Handle a ping message by another player / storyteller
	 * 处理其他玩家/说书人发出的ping消息
	 * @param playerIdOrCount
	 * @param latency
	 * @private
	 */
	_handlePing([playerIdOrCount = 0, latency, isUpdatePlayers = true] = [] ) {
		const now = new Date().getTime();
		if (!this._isSpectator) {
			// remove players that haven't sent a ping in twice the timespan
			// 删除在两倍时间间隔内未发送ping的玩家
			for (let player in this._players) {
				if (now - this._players[player] > this._pingInterval * 2) {
					delete this._players[player];
					delete this._pings[player];
				}
			}
			// remove claimed seats from players that are no longer connected
			// 从不再连接的玩家中移除声称的座位
			// if (isUpdatePlayers) {
			// 	this._store.state.players.players.forEach(player => {
			// 		if (player.id && !this._players[player.id]) {
			// 			this._store.commit("players/update", {
			// 				player,
			// 				property: "id",
			// 				value: "",
			// 				userInfo: null
			// 			});
			// 		}
			// 	});
			// }

			// store new player data
			// 存储新玩家数据
			if (playerIdOrCount) {
				this._players[playerIdOrCount] = now;
				const ping = parseInt(latency, 10);
				if (ping && ping > 0 && ping < 30 * 1000) {
					// ping to Players
					this._pings[playerIdOrCount] = ping;
					const pings = Object.values(this._pings);
					this._store.commit(
						"session/setPing",
						Math.round(pings.reduce((a, b) => a + b, 0) / pings.length)
					);
				}
			}
		} else if (latency) {
			// ping to ST
			this._store.commit("session/setPing", parseInt(latency, 10));
		}
		// update player count
		if (!this._isSpectator || playerIdOrCount) {
			this._store.commit(
				"session/setPlayerCount",
				this._isSpectator ? playerIdOrCount : Object.keys(this._players).length
			);
		}
	}

	/**
	 * Handle a player leaving the sessions. ST only
	 * 处理一个离开房间的玩家，只有ST
	 * @param playerId
	 * @private
	 */
	_handleBye(playerId) {
		if (this._isSpectator) return;
		delete this._players[playerId];
		this._store.commit(
			"session/setPlayerCount",
			Object.keys(this._players).length
		);
	}

	/**
	 * 坐下，需要说书人确认
	 * 已经被占用的座位无法坐下
	 * @param seat either -1 to vacate or the index of the seat claimed
	 * 空出-1或申请座位的索引
	 */
	claimSeat(seat) {
		if (!this._isSpectator) return;
		const players = this._store.state.players.players;
		let userInfo = this._store.state.session.playerUserInfo ? this._store.state.session.playerUserInfo : null;

		if (players.length > seat && (seat < 0 || !players[seat].id)) {
			this._send("claim", [seat, this._store.state.session.playerId, userInfo]);
		}
	}

	/**
	 * Update a player id associated with that seat.
	 * 更新与该座位关联的玩家id。
	 * @param index seat index or -1
	 * 座位index或-1
	 * @param value playerId to add / remove
	 * 要添加或删除的玩家Id
	 * @param userInfo playerId to add / remove
	 * 要添加或删除的玩家信息
	 * @private
	 */
	_updateSeat([index, value, userInfo]) {
		if (this._isSpectator) return;
		const property = "id";
		const players = this._store.state.players.players;
		// remove previous seat
		const oldIndex = players.findIndex(({
			id
		}) => id === value);
		if (oldIndex >= 0 && oldIndex !== index) {
			this._store.commit("players/update", {
				player: players[oldIndex],
				property,
				value: "",
				userInfo: null
			});
		}
		// add playerId to new seat
		// 将玩家Id添加到新座位
		if (index >= 0) {
			const player = players[index];
			if (!player) return;
			this._store.commit("players/update", {
				player,
				property,
				value,
				userInfo: userInfo
			});
		}
		// update player session list as if this was a ping
		// 更新玩家会话列表，就像这是ping一样
		this._handlePing([true, value, 0]);
	}

	/**
	 * Distribute player roles to all seated players in a direct message.
	 * 通过直接消息将玩家角色分配给所有坐着的玩家。
	 * This will be split server side so that each player only receives their own (sub)message.
	 * 这将在服务器端分开，这样每个玩家只接收他们自己的（子）消息。
	 */
	distributeRoles() {
		if (this._isSpectator) return;
		const message = {};
		this._store.state.players.players.forEach((player, index) => {
			if (player.id && player.role) {
				message[player.id] = [
					"player",
					{
						index,
						property: "role",
						value: player.role.id,
						userInfo: player.userInfo
					}
				];
			}
		});
		if (Object.keys(message).length) {
			this._send("direct", message);
		}
	}

	/**
	 * A player nomination. ST only
	 * 玩家提名，只有ST
	 * This also syncs the voting speed to the players.
	 * 这也会将投票进度与玩家同步。
	 * Payload can be an object with {nomination} property or just the nomination itself, or undefined.
	 * 有效负载可以是具有{namitment}属性的对象，也可以只是指定本身，或者是未定义的对象。
	 * @param payload [nominator, nominee]|{nomination}
	 */
	nomination(payload) {
		if (this._isSpectator) return;
		const nomination = payload ? payload.nomination || payload : payload;
		const players = this._store.state.players.players;
		if (
			!nomination ||
			(players.length > nomination[0] && players.length > nomination[1])
		) {
			this.setVotingSpeed(this._store.state.session.votingSpeed);
			this._send("nomination", nomination);
		}
	}

	/**
	 * Set the isVoteInProgress status. ST only
	 * 设置isVoteInProgress状态， 只有ST
	 */
	setVoteInProgress() {
		if (this._isSpectator) return;
		this._send("isVoteInProgress", this._store.state.session.isVoteInProgress);
	}

	/**
	 * Send the isNight status. ST only
	 * 发送isNight状态，只有说书人
	 */
	setIsNight() {
		if (this._isSpectator) return;
		this._send("isNight", this._store.state.grimoire.isNight);
	}

	/**
	 * Send the isVoteHistoryAllowed state. ST only
	 * 发送isVoteHistoryAllowed状态，只有说书人
	 */
	setVoteHistoryAllowed() {
		if (this._isSpectator) return;
		this._send(
			"isVoteHistoryAllowed",
			this._store.state.session.isVoteHistoryAllowed
		);
	}

	/**
	 * Send the voting speed. ST only
	 * 发送投票进度，只有说书人
	 * @param votingSpeed voting speed in seconds, minimum 1
	 */
	setVotingSpeed(votingSpeed) {
		if (this._isSpectator) return;
		if (votingSpeed) {
			this._send("votingSpeed", votingSpeed);
		}
	}

	/**
	 * Set which player is on the block. ST only
	 * 设定那个玩家在死亡区里，只有说书人
	 * @param playerIndex, player id or -1 for empty
	 */
	setMarked(playerIndex) {
		if (this._isSpectator) return;
		this._send("marked", playerIndex);
	}

	/**
	 * Clear the vote history for everyone. ST only
	 * 清除每个人的投票记录，只有说书人
	 */
	clearVoteHistory() {
		if (this._isSpectator) return;
		this._send("clearVoteHistory");
	}

	/**
	 * Send a vote. Player or ST
	 * 投票，玩家或者说书人
	 * @param index Seat of the player
	 * 玩家的座位编号
	 * @param sync Flag whether to sync this vote with others or not
	 * 标记是否把当前投票与其他人同步
	 */
	vote([index]) {
		const player = this._store.state.players.players[index];
		if (
			this._store.state.session.playerId === player.id ||
			!this._isSpectator
		) {
			// send vote only if it is your own vote or you are the storyteller
			// 只有当投票是你自己的或者你是说书人时，才发送投票
			this._send("vote", [
				index,
				this._store.state.session.votes[index],
				!this._isSpectator
			]);
		}
	}

	/**
	 * Handle an incoming vote, but only if it is from ST or unlocked.
	 * 处理即将到来的投票，但前提是它来自ST或未锁定。
	 * @param index
	 * @param vote
	 * @param fromST
	 */
	_handleVote([index, vote, fromST]) {
		const {
			session,
			players
		} = this._store.state;
		const playerCount = players.players.length;
		const indexAdjusted =
			(index - 1 + playerCount - session.nomination[1]) % playerCount;
		if (fromST || indexAdjusted >= session.lockedVote - 1) {
			this._store.commit("session/vote", [index, vote]);
		}
	}

	/**
	 * Lock a vote. ST only
	 * 锁定投票，只有说书人
	 */
	lockVote() {
		if (this._isSpectator) return;
		const {
			lockedVote,
			votes,
			nomination
		} = this._store.state.session;
		const {
			players
		} = this._store.state.players;
		const index = (nomination[1] + lockedVote - 1) % players.length;
		this._send("lock", [this._store.state.session.lockedVote, votes[index]]);
	}

	/**
	 * Update vote lock and the locked vote, if it differs. Player only
	 * 更新投票锁定和锁定的投票（如果不同）。仅限玩家
	 * @param lock
	 * @param vote
	 * @private
	 */
	_handleLock([lock, vote]) {
		if (!this._isSpectator) return;
		this._store.commit("session/lockVote", lock);
		if (lock > 1) {
			const {
				lockedVote,
				nomination
			} = this._store.state.session;
			const {
				players
			} = this._store.state.players;
			const index = (nomination[1] + lockedVote - 1) % players.length;
			if (this._store.state.session.votes[index] !== vote) {
				this._store.commit("session/vote", [index, vote]);
			}
		}
	}

	/**
	 * Swap two player seats. ST only
	 * 交换玩家座位，仅限说书人
	 * @param payload
	 */
	swapPlayer(payload) {
		if (this._isSpectator) return;
		this._send("swap", payload);
	}

	/**
	 * Move a player to another seat. ST only
	 * 把一个玩家移到另一个座位上，仅限说书人
	 * @param payload
	 */
	movePlayer(payload) {
		if (this._isSpectator) return;
		this._send("move", payload);
	}

	/**
	 * Remove a player. ST only
	 * 删除一名玩家，仅限说书人
	 * @param payload
	 */
	removePlayer(payload) {
		if (this._isSpectator) return;
		this._send("remove", payload);
	}
}

export default store => {
	// setup
	const session = new LiveSession(store);

	// 监听到mutations
	store.subscribe(({
		type,
		payload
	}, state) => {
		switch (type) {
			case "session/setSessionId":
				if (state.session.sessionId) {
					session.connect(state.session.sessionId);
				} else {
					window.location.hash = "";
					session.disconnect();
				}
				break;
			case "session/claimSeat":
				session.claimSeat(payload);
				break;
			case "session/distributeRoles":
				if (payload) {
					session.distributeRoles();
				}
				break;
			case "session/nomination":
			case "session/setNomination":
				session.nomination(payload);
				break;
			case "session/setVoteInProgress":
				session.setVoteInProgress(payload);
				break;
			case "session/voteSync":
				session.vote(payload);
				break;
			case "session/lockVote":
				session.lockVote();
				break;
			case "session/setVotingSpeed":
				session.setVotingSpeed(payload);
				break;
			case "session/clearVoteHistory":
				session.clearVoteHistory();
				break;
			case "session/setVoteHistoryAllowed":
				session.setVoteHistoryAllowed();
				break;
			case "toggleNight":
				session.setIsNight();
				break;
			case "setEdition":
				session.sendEdition();
				break;
			case "players/setFabled":
				session.sendFabled();
				break;
			case "session/setMarkedPlayer":
				session.setMarked(payload);
				break;
			case "players/swap":
				session.swapPlayer(payload);
				break;
			case "players/move":
				session.movePlayer(payload);
				break;
			case "players/remove":
				session.removePlayer(payload);
				break;
			case "players/set":
			case "players/clear":
			case "players/add":
				session.sendGamestate("", true);
				break;
			case "players/update":
				if (payload.property === "pronouns") {
					session.sendPlayerPronouns(payload);
				} else {
					session.sendPlayer(payload);
				}
				break;
		}
	});

	// check for session Id in hash
	// 检查会话ID
	const sessionId = window.location.hash.substr(1);
	if (sessionId) {
		store.commit("session/setSpectator", true);
		store.commit("session/setSessionId", sessionId);
		store.commit("toggleGrimoire", false);
	}
};